import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import WaitlistModal from './WaitlistModal'; // BETA MODE

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isDark, toggleTheme } = useTheme();
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [activeSection, setActiveSection] = useState<string>('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false); // BETA MODE

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    if (location.pathname !== '/') {
      setActiveSection('');
      return;
    }

    const handleScroll = () => {
      const sections = ['pricing', 'faq'];
      const scrollPosition = window.scrollY + 100; // Offset for navbar height

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            return;
          }
        }
      }
      setActiveSection('');
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 transition-colors duration-200">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/20 dark:bg-gray-900/20 backdrop-blur-2xl border-b border-white/30 dark:border-gray-700/30 z-50 shadow-lg shadow-black/5">
        {/* Glass reflection effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent dark:via-white/5"></div>
        <div className="relative z-10 w-full px-12 sm:px-16 lg:px-24">
          <div className="flex items-center justify-between h-24 w-full">
            {/* Logo and Navigation - Left Side */}
            <div className="flex items-center space-x-8">
              {/* Logo */}
              <div className="flex-shrink-0">
                <Link to="/" className="flex items-center">
                  <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent whitespace-nowrap">
                    Svarra AI
                  </span>
                </Link>
              </div>

              {/* Desktop Navigation - Next to Logo */}
              <div className="hidden md:flex items-center space-x-8">
                <Link
                  to="/"
                  className={`text-base font-medium transition-colors ${
                    isActive('/') && activeSection === ''
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                  onClick={(e) => {
                    if (location.pathname === '/') {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  Home
                </Link>
                <Link
                  to="/whats-new"
                  className={`text-base font-medium transition-colors ${
                    isActive('/whats-new')
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  What's New
                </Link>
                <Link
                  to="/our-impact"
                  className={`text-base font-medium transition-colors ${
                    isActive('/our-impact')
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  Our Impact
                </Link>
                <a
                  href={location.pathname === '/' ? '#pricing' : '/#pricing'}
                  className={`text-base font-medium transition-colors ${
                    activeSection === 'pricing'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                  onClick={(e) => {
                    if (location.pathname !== '/') {
                      e.preventDefault();
                      navigate('/', { replace: true });
                      
                      // Wait for navigation and element to be available
                      const scrollToPricing = () => {
                        const element = document.getElementById('pricing');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          // If element not found, try again after a short delay
                          setTimeout(scrollToPricing, 50);
                        }
                      };
                      
                      setTimeout(scrollToPricing, 200);
                    }
                  }}
                >
                  Pricing
                </a>
                <Link
                  to="/about"
                  className={`text-base font-medium transition-colors ${
                    isActive('/about')
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  About
                </Link>
                <a
                  href={location.pathname === '/' ? '#faq' : '/#faq'}
                  className={`text-base font-medium transition-colors ${
                    activeSection === 'faq'
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                  onClick={(e) => {
                    if (location.pathname !== '/') {
                      e.preventDefault();
                      navigate('/', { replace: true });
                      
                      // Wait for navigation and element to be available
                      const scrollToFAQ = () => {
                        const element = document.getElementById('faq');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          // If element not found, try again after a short delay
                          setTimeout(scrollToFAQ, 50);
                        }
                      };
                      
                      setTimeout(scrollToFAQ, 200);
                    }
                  }}
                >
                  FAQ
                </a>
                <Link
                  to="/contact"
                  className={`text-base font-medium transition-colors ${
                    isActive('/contact')
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  Contact
                </Link>
              </div>
            </div>

            {/* Right side - Theme toggle and Auth - Far Right */}
            <div className="flex items-center space-x-5 flex-shrink-0">
              {/* BETA MODE: Join Waitlist Button - Only show for non-authenticated users */}
              {!isAuthenticated && (
                <button
                  onClick={() => setShowWaitlistModal(true)}
                  className="hidden md:inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-all duration-200"
                >
                  Join Waitlist
                </button>
              )}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Auth Section - Hidden on mobile, shown on desktop */}
              {isAuthenticated ? (
                <div className="hidden md:flex items-center space-x-5">
                  <Link
                    to="/dashboard"
                    className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={logout}
                    className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Logout
                  </button>
                  <div className="text-base text-gray-600 dark:text-gray-400">
                    {user?.firstName} {user?.lastName}
                  </div>
                </div>
              ) : (
                <div className="hidden md:flex items-center space-x-5">
                  <Link
                    to="/login"
                    className="text-base font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-2 rounded-lg text-base font-medium hover:shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    Create Account
                  </Link>
                </div>
              )}

              {/* Mobile menu button */}
              <button 
                className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-white/30 dark:border-gray-700/30">
            <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-4">
              <Link
                to="/"
                className={`block text-lg font-medium transition-colors ${
                  isActive('/') && activeSection === ''
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  if (location.pathname === '/') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
              >
                Home
              </Link>

              <Link
                to="/whats-new"
                className={`block text-lg font-medium transition-colors ${
                  isActive('/whats-new')
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                What's New
              </Link>

              <Link
                to="/our-impact"
                className={`block text-lg font-medium transition-colors ${
                  isActive('/our-impact')
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Our Impact
              </Link>

              <a
                href={location.pathname === '/' ? '#pricing' : '/#pricing'}
                className={`block text-lg font-medium transition-colors ${
                  activeSection === 'pricing'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={(e) => {
                  setIsMobileMenuOpen(false);
                  if (location.pathname !== '/') {
                    e.preventDefault();
                    navigate('/', { replace: true });
                    setTimeout(() => {
                      const element = document.getElementById('pricing');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }
                }}
              >
                Pricing
              </a>
              
              <Link
                to="/about"
                className={`block text-lg font-medium transition-colors ${
                  isActive('/about') 
                    ? 'text-primary-600 dark:text-primary-400' 
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About
              </Link>
              
              <a
                href={location.pathname === '/' ? '#faq' : '/#faq'}
                className={`block text-lg font-medium transition-colors ${
                  activeSection === 'faq'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={(e) => {
                  setIsMobileMenuOpen(false);
                  if (location.pathname !== '/') {
                    e.preventDefault();
                    navigate('/', { replace: true });
                    setTimeout(() => {
                      const element = document.getElementById('faq');
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }
                }}
              >
                FAQ
              </a>
              
              <Link
                to="/contact"
                className={`block text-lg font-medium transition-colors ${
                  isActive('/contact')
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>

              {/* BETA MODE: Join Waitlist Button - Mobile */}
              {!isAuthenticated && (
                <button
                  onClick={() => {
                    setShowWaitlistModal(true);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-lg font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                >
                  Join Waitlist
                </button>
              )}

              {/* Mobile Auth Section */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                {isAuthenticated ? (
                  <div className="space-y-3">
                    <Link
                      to="/dashboard"
                      className="block text-lg font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left text-lg font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      Logout
                    </button>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {user?.firstName} {user?.lastName}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Link
                      to="/login"
                      className="block text-lg font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="block bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-4 py-3 rounded-lg text-lg font-medium text-center hover:shadow-lg transition-all duration-200"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Create Account
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="pt-24">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative bg-gray-900/80 dark:bg-gray-950/80 backdrop-blur-md text-white border-t border-white/10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <span className="text-2xl font-bold bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
                Svarra AI
              </span>
              <p className="mt-4 text-gray-400 max-w-md">
                AI assistants for local businesses. Book clients, answer questions, 
                and handle scheduling—all automatically.
              </p>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Product</h3>
              <div className="space-y-2">
                <Link to="/whats-new" className="block text-gray-400 hover:text-white transition-colors">
                  What's New
                </Link>
                <a
                  href={location.pathname === '/' ? '#pricing' : '/#pricing'}
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={(e) => {
                    if (location.pathname !== '/') {
                      e.preventDefault();
                      navigate('/', { replace: true });

                      // Wait for navigation and element to be available
                      const scrollToPricing = () => {
                        const element = document.getElementById('pricing');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          // If element not found, try again after a short delay
                          setTimeout(scrollToPricing, 50);
                        }
                      };

                      setTimeout(scrollToPricing, 200);
                    }
                  }}
                >
                  Pricing
                </a>
                <a
                  href={location.pathname === '/' ? '#faq' : '/#faq'}
                  className="block text-gray-400 hover:text-white transition-colors"
                  onClick={(e) => {
                    if (location.pathname !== '/') {
                      e.preventDefault();
                      navigate('/', { replace: true });

                      // Wait for navigation and element to be available
                      const scrollToFAQ = () => {
                        const element = document.getElementById('faq');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          // If element not found, try again after a short delay
                          setTimeout(scrollToFAQ, 50);
                        }
                      };

                      setTimeout(scrollToFAQ, 200);
                    }
                  }}
                >
                  FAQ
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Company</h3>
              <div className="space-y-2">
                <Link to="/about" className="block text-gray-400 hover:text-white transition-colors">
                  About
                </Link>
                <Link to="/our-impact" className="block text-gray-400 hover:text-white transition-colors">
                  Our Impact
                </Link>
                <Link to="/contact" className="block text-gray-400 hover:text-white transition-colors">
                  Contact
                </Link>
                <Link to="/privacy-policy" className="block text-gray-400 hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <a href="mailto:support@svarra.ai" className="block text-gray-400 hover:text-white transition-colors">
                  support@svarra.ai
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400">
              © {currentYear} Svarra AI. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* BETA MODE: Waitlist Modal */}
      <WaitlistModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
    </div>
  );
};

export default Layout;